import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivationEnd, Params, Router } from '@angular/router';

// Libs
import { BooleanString } from '@libs/types';

// Self
import { UserType } from './user-data';
import { v4 as uuid } from 'uuid';
import { getCookie } from './get-cookie';
import { deleteCookie } from './delete-cookie';
import { appInstanceId, deviceTimezone } from '@libs/data';
import { APP_CONFIG } from '@libs/configs';
import { filter, map, startWith } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

const isActivationWithRefCode = (event) =>
  event instanceof ActivationEnd &&
  (event as ActivationEnd)?.snapshot?.queryParams?.['ref'];

const sessionRefCode = 'snagajob-refcode';

@Injectable({
  providedIn: 'root',
})
export class UserDataService {
  private USER_TYPE = 'user-type';
  private IS_ECOMM = 'is-ecomm';
  private SESSION_PROMO_CODE = 'snagajob-promocode';
  private COOKIE_USER_UUID_EXPIRE = 365 * 5;
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private appConfig = inject(APP_CONFIG);
  private router = inject(Router);

  public readonly appInstanceId =
    getCookie(document.cookie, appInstanceId) ?? uuid();

  public readonly deviceTimezone =
    getCookie(document.cookie, deviceTimezone) ??
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  public readonly refCode = toSignal(
    this.router.events.pipe(
      filter(isActivationWithRefCode),
      map((event: ActivationEnd) => ({
        refCode: event?.snapshot?.queryParams?.['ref'],
        refSource: 'URL',
      })),
      startWith(
        this.isBrowser && typeof sessionStorage !== 'undefined'
          ? {
              refCode: sessionStorage.getItem(sessionRefCode) ?? 'no-ref-code',
              refSource: 'STORAGE',
            }
          : null,
      ),
    ),
  );

  constructor() {
    if (this.appInstanceId != getCookie(document.cookie, appInstanceId)) {
      // fixes a bug where the cookie was set on the wrong domain - e.g., employer.snagajob.com, instead of .snagajob.com
      deleteCookie(appInstanceId);
      const expires = new Date();
      expires.setDate(expires.getDate() + this.COOKIE_USER_UUID_EXPIRE);
      document.cookie = `${appInstanceId}=${this.appInstanceId};expires=${expires};domain=${this.appConfig.rootCookieDomain};`;
    }

    if (this.deviceTimezone != getCookie(document.cookie, deviceTimezone)) {
      // fixes a bug where the cookie was set on the wrong domain - e.g., employer.snagajob.com, instead of .snagajob.com
      deleteCookie(deviceTimezone);
      const expires = new Date();
      expires.setDate(expires.getDate() + this.COOKIE_USER_UUID_EXPIRE);
      document.cookie = `${deviceTimezone}=${this.deviceTimezone};expires=${expires};domain=${this.appConfig.rootCookieDomain};`;
    }
  }

  /**
   * The PWA uses standalone mode. If navigator is in standalone, it means user is using PWA. Otherwise, user is in web
   */
  setUserType(): void {
    let displayMode: UserType = 'web';
    const mqStandAlone = '(display-mode: standalone)';
    if (
      (navigator as any).standalone ||
      window.matchMedia(mqStandAlone).matches
    ) {
      displayMode = 'pwa';
    }

    sessionStorage.setItem(this.USER_TYPE, displayMode);
  }

  getUserType(): UserType {
    return sessionStorage.getItem(this.USER_TYPE) as UserType;
  }

  /**
   * Save isEcomm boolean value to session storage. This is used by Adobe
   */
  setEcommStatus(isEcomm?: boolean): void {
    sessionStorage.setItem(
      this.IS_ECOMM,
      isEcomm ? BooleanString.True : BooleanString.False,
    );
  }

  /*
   * Get the promo code to give discount for Ecomm
   */
  getPromoCode(): string {
    return sessionStorage.getItem(this.SESSION_PROMO_CODE);
  }

  /**
   * Saves promo code from the url into session storage
   * @param params query parameters
   */
  setPromoCode(params?: Params): void {
    const promoCode: string = params['promo'];
    if (promoCode) {
      sessionStorage.setItem(this.SESSION_PROMO_CODE, promoCode);
    }
  }
}
